import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, vModelText as _vModelText, withDirectives as _withDirectives, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "assignment-details" }
const _hoisted_2 = {
  key: 0,
  class: "linker-wrapper"
}
const _hoisted_3 = { class: "assignment-content" }
const _hoisted_4 = { class: "text-content" }
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = { class: "details-section" }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = { key: 1 }
const _hoisted_9 = { class: "details-section" }
const _hoisted_10 = { class: "gallery-header" }
const _hoisted_11 = {
  key: 0,
  class: "gallery-edit-mode"
}
const _hoisted_12 = { class: "edit-form" }
const _hoisted_13 = { class: "edit-buttons" }
const _hoisted_14 = ["disabled"]
const _hoisted_15 = {
  key: 1,
  class: "gallery-display-mode"
}
const _hoisted_16 = { class: "gallery-actions" }
const _hoisted_17 = ["href"]
const _hoisted_18 = {
  key: 0,
  class: "action"
}
const _hoisted_19 = {
  key: 1,
  class: "action"
}
const _hoisted_20 = {
  key: 2,
  class: "no-gallery-notice"
}
const _hoisted_21 = {
  key: 0,
  class: "assignment-images"
}
const _hoisted_22 = { class: "images-grid" }
const _hoisted_23 = ["src", "onClick"]
const _hoisted_24 = { class: "details-section" }
const _hoisted_25 = {
  key: 0,
  class: "images"
}
const _hoisted_26 = ["src", "onClick"]
const _hoisted_27 = {
  key: 1,
  class: "images thumbnails"
}
const _hoisted_28 = ["src"]
const _hoisted_29 = { key: 2 }
const _hoisted_30 = {
  key: 1,
  class: "details-section"
}
const _hoisted_31 = { class: "details-section" }
const _hoisted_32 = { class: "teacher-notes-header" }
const _hoisted_33 = {
  key: 0,
  class: "lock-icon",
  title: "Huomiot lukittu arvioinnin ajaksi"
}
const _hoisted_34 = ["readonly"]
const _hoisted_35 = ["disabled"]

import AssignmentDataLinker from "@/components/AssignmentDataLinker.vue";
import BaseIcon from "@/components/BaseIcon.vue";
import { marked } from "marked";
import { computed, ref, watch } from "vue";
import { apiUrl } from "@/config/api";

interface ImageDownload {
  url: string;
  title: string;
  fileType: string;
}

interface AssignmentDetails {
  id?: number;
  isConfirmed?: boolean;
  otsikko?: string;
  alaotsikko?: string | null;
  lukukausi?: string;
  tehtävänanto?: string;
  arviointiperusteet?: string[];
  kuvat?: string[];
  peerGalleryDriveLink?: string | null;
}

interface LearningUnit {
  title?: string;
}

interface Section {
  title?: string;
  description?: string;
}

interface Answer {
  description?: string;
  answerData?: {
    downloads?: ImageDownload[];
  };
}

interface AssignmentData {
  userName?: string;
  assignmentDetails?: AssignmentDetails;
  learningUnit?: LearningUnit & { id?: string };
  section?: Section;
  answers?: Answer[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AssignmentDetails',
  props: {
    assignmentData: {},
    isGalleryOpen: { type: Boolean },
    teacherNotes: {},
    isGradingStarted: { type: Boolean },
    isPeerGalleryOpen: { type: Boolean }
  },
  emits: [
  "open-image-gallery",
  "close-image-gallery",
  "open-assignment-gallery",
  "update-teacher-notes",
  "start-grading",
  "reset-grading",
  "toggle-embedded-gallery",
  "enable-gallery-edit",
  "update-gallery-link",
  "toggle-peer-gallery",
  "assignment-confirmed",
],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const assignmentText = computed(() => {
  if (!props.assignmentData) {
    return "Tehtävänantoa ei löytynyt.";
  }

  const assignment_text =
    props.assignmentData.assignmentDetails?.tehtävänanto ||
    props.assignmentData.section?.description ||
    "Tehtävänantoa ei löytynyt.";

  const text = `
  # ${props.assignmentData.learningUnit?.title || "Tehtävän nimeä ei löytynyt"}

  ## ${props.assignmentData.section?.title || "Otsikkoa ei löytynyt"}

  ${assignment_text}
  `;

  return marked(text);
});

const emit = __emit;

const needsLinking = computed(
  () =>
    !props.assignmentData.assignmentDetails ||
    !props.assignmentData.assignmentDetails.isConfirmed,
);

const onAssignmentConfirmed = (details: AssignmentDetails) => {
  emit("assignment-confirmed", details);
};

const isEditingGalleryLink = ref(false);
const editingGalleryLink = ref("");
const isSavingGalleryLink = ref(false);

const teacherNotesText = ref(props.teacherNotes || "");

// Watch for external changes to teacherNotes prop
watch(
  () => props.teacherNotes,
  (newValue) => {
    teacherNotesText.value = newValue || "";
  },
);

// Emit changes when user types
watch(teacherNotesText, (newValue) => {
  emit("update-teacher-notes", newValue);
});

// Check if teacher notes meet minimum requirements for grading
const canStartGrading = computed(() => {
  const text = teacherNotesText.value.trim();
  if (!text) return false;

  // Check character count (at least 100 characters)
  if (text.length >= 100) return true;

  // Check line count (at least 3 lines)
  const lines = text.split("\n").filter((line) => line.trim().length > 0);
  if (lines.length >= 3) return true;

  // Check word count (at least 15 words)
  const words = text.split(/\s+/).filter((word) => word.length > 0);
  if (words.length >= 15) return true;

  return false;
});

const startGrading = () => {
  if (canStartGrading.value) {
    emit("start-grading", teacherNotesText.value);
  }
};

const resetGrading = () => {
  emit("reset-grading");
};

const allImagesCount = computed(() => {
  return props.assignmentData.answers?.[0]?.answerData?.downloads?.length || 0;
});

const allImages = computed(() => {
  return props.assignmentData.answers?.[0]?.answerData?.downloads || [];
});

const assignmentImages = computed(() => {
  return props.assignmentData.assignmentDetails?.kuvat || [];
});

const openImage = (image: ImageDownload) => {
  emit("open-image-gallery", image);
};

const openAssignmentImage = (imageUrl: string) => {
  // Create a mock image object for the gallery
  const imageObject = {
    url: imageUrl,
    title: "Tehtävän kuva",
    fileType: "image/jpeg",
  };

  // Create an array of all assignment images for the gallery
  const allAssignmentImages = assignmentImages.value.map((url, index) => ({
    url: url,
    title: `Tehtävän kuva ${index + 1}`,
    fileType: "image/jpeg",
  }));

  // Emit to separate assignment gallery
  emit("open-assignment-gallery", imageObject, allAssignmentImages);
};

const closeGallery = () => {
  emit("close-image-gallery");
};

const openPeerGallerySection = () => {
  const driveUrl = props.assignmentData.assignmentDetails?.peerGalleryDriveLink;
  if (driveUrl) {
    const embeddableUrl = getEmbeddableGalleryUrl(driveUrl);
    emit("toggle-peer-gallery", embeddableUrl);
  }
};

const getEmbeddableGalleryUrl = (driveUrl: string): string => {
  // Convert Google Drive sharing URL to embeddable format
  // From: https://drive.google.com/drive/folders/FOLDER_ID?usp=sharing
  // To: https://drive.google.com/embeddedfolderview?id=FOLDER_ID#grid

  try {
    const url = new URL(driveUrl);
    if (
      url.hostname === "drive.google.com" &&
      url.pathname.includes("/folders/")
    ) {
      const folderId = url.pathname.split("/folders/")[1]?.split("/")[0];
      if (folderId) {
        return `https://drive.google.com/embeddedfolderview?id=${folderId}#grid`;
      }
    }
  } catch {
    console.warn("Invalid drive URL:", driveUrl);
  }

  // Fallback to original URL if conversion fails
  return driveUrl;
};

const enableGalleryEdit = () => {
  editingGalleryLink.value =
    props.assignmentData.assignmentDetails?.peerGalleryDriveLink || "";
  isEditingGalleryLink.value = true;
};

const cancelGalleryEdit = () => {
  isEditingGalleryLink.value = false;
  editingGalleryLink.value = "";
};

const saveGalleryLink = async () => {
  if (!props.assignmentData.assignmentDetails?.id) {
    console.error("No assignment ID available");
    return;
  }

  isSavingGalleryLink.value = true;

  try {
    const response = await fetch(
      apiUrl(`/assignments/${props.assignmentData.assignmentDetails.id}`),
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          peer_gallery_drive_link: editingGalleryLink.value.trim() || null,
        }),
      },
    );

    if (!response.ok) {
      const errorData = (await response.json()) as { error?: string };
      throw new Error(errorData.error || "Failed to update assignment");
    }

    const result = (await response.json()) as {
      assignment: { peer_gallery_drive_link: string | null };
    };
    console.log("✅ Gallery link updated:", result);

    // Emit the update instead of mutating props
    emit("update-gallery-link", editingGalleryLink.value.trim() || null);

    isEditingGalleryLink.value = false;
    editingGalleryLink.value = "";
  } catch (error) {
    console.error("Error updating gallery link:", error);
    alert(
      "Virhe galleria-linkin tallentamisessa: " +
        (error instanceof Error ? error.message : "Tuntematon virhe"),
    );
  } finally {
    isSavingGalleryLink.value = false;
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (needsLinking.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(AssignmentDataLinker, {
            "learning-unit-id": __props.assignmentData.learningUnit?.id || '',
            "existing-assignment-id": __props.assignmentData.assignmentDetails?.id,
            "existing-data": __props.assignmentData.assignmentDetails,
            onConfirmed: onAssignmentConfirmed
          }, null, 8, ["learning-unit-id", "existing-assignment-id", "existing-data"])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", {
          class: "details-section",
          innerHTML: assignmentText.value
        }, null, 8, _hoisted_5),
        _createElementVNode("div", _hoisted_6, [
          _cache[2] || (_cache[2] = _createElementVNode("h2", null, "Arviointiperusteet", -1)),
          (
              (__props.assignmentData.assignmentDetails?.arviointiperusteet?.length ||
                0) > 0
            )
            ? (_openBlock(), _createElementBlock("ul", _hoisted_7, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.assignmentData.assignmentDetails
                ?.arviointiperusteet, (criterion, index) => {
                  return (_openBlock(), _createElementBlock("li", { key: index }, _toDisplayString(criterion), 1))
                }), 128))
              ]))
            : (_openBlock(), _createElementBlock("p", _hoisted_8, "Arviointiperusteita ei löytynyt."))
        ]),
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            _cache[3] || (_cache[3] = _createElementVNode("h2", null, "Kotitehtävägalleria", -1)),
            (__props.assignmentData.assignmentDetails?.id)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  onClick: enableGalleryEdit,
                  class: "edit-icon-button header-edit",
                  title: "Muokkaa linkkiä"
                }, [
                  _createVNode(BaseIcon, {
                    name: "edit",
                    size: 16
                  })
                ]))
              : _createCommentVNode("", true)
          ]),
          (isEditingGalleryLink.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                _createElementVNode("div", _hoisted_12, [
                  _cache[4] || (_cache[4] = _createElementVNode("label", { for: "gallery-link-input" }, "Google Drive -kansion linkki:", -1)),
                  _withDirectives(_createElementVNode("input", {
                    id: "gallery-link-input",
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((editingGalleryLink).value = $event)),
                    type: "url",
                    placeholder: "https://drive.google.com/drive/folders/...",
                    class: "gallery-link-input"
                  }, null, 512), [
                    [_vModelText, editingGalleryLink.value]
                  ]),
                  _createElementVNode("div", _hoisted_13, [
                    _createElementVNode("button", {
                      onClick: saveGalleryLink,
                      disabled: isSavingGalleryLink.value,
                      class: "save-button"
                    }, _toDisplayString(isSavingGalleryLink.value ? "Tallennetaan..." : "Tallenna"), 9, _hoisted_14),
                    _createElementVNode("button", {
                      onClick: cancelGalleryEdit,
                      class: "cancel-button"
                    }, " Peruuta ")
                  ])
                ]),
                _cache[5] || (_cache[5] = _createElementVNode("div", { class: "setup-help" }, [
                  _createElementVNode("p", null, [
                    _createElementVNode("a", {
                      href: "https://drive.google.com/drive/folders/1uDd-ZX2SCEJm9HacuL4CrBnxJL_NxGs_",
                      target: "_blank",
                      class: "drive-link"
                    }, " Siirry Google Driveen "),
                    _createTextVNode(" ja luo tai etsi tehtäväkohtainen kansio. Jaa kansio julkisesti ja kopioi linkki. ")
                  ])
                ], -1))
              ]))
            : (__props.assignmentData.assignmentDetails?.peerGalleryDriveLink)
              ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                  _createElementVNode("div", _hoisted_16, [
                    _createElementVNode("a", {
                      href: __props.assignmentData.assignmentDetails.peerGalleryDriveLink,
                      target: "_blank",
                      class: "drive-link-button"
                    }, [
                      _createVNode(BaseIcon, {
                        name: "google-drive",
                        size: 16,
                        "class-name": "drive-icon"
                      }),
                      _cache[6] || (_cache[6] = _createElementVNode("span", null, "Tarkastele Drivessä", -1)),
                      _cache[7] || (_cache[7] = _createElementVNode("svg", {
                        class: "external-link-icon",
                        viewBox: "0 0 24 24",
                        fill: "currentColor"
                      }, [
                        _createElementVNode("path", { d: "M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z" })
                      ], -1))
                    ], 8, _hoisted_17),
                    _createElementVNode("button", {
                      onClick: openPeerGallerySection,
                      class: "gallery-embed-button primary"
                    }, [
                      (__props.isPeerGalleryOpen)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_18, "Sulje"))
                        : (_openBlock(), _createElementBlock("span", _hoisted_19, "Avaa")),
                      _cache[8] || (_cache[8] = _createTextVNode(" galleria ", -1))
                    ])
                  ])
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_20, [
                  _cache[10] || (_cache[10] = _createElementVNode("p", null, "Kotitehtävägalleriaa ei ole asetettu.", -1)),
                  _createElementVNode("div", { class: "setup-instructions" }, [
                    _cache[9] || (_cache[9] = _createElementVNode("p", null, [
                      _createElementVNode("a", {
                        href: "https://drive.google.com/drive/folders/1uDd-ZX2SCEJm9HacuL4CrBnxJL_NxGs_",
                        target: "_blank",
                        class: "drive-link"
                      }, " Siirry Google Driveen "),
                      _createTextVNode(" ja luo tai etsi tehtäväkohtainen kansio. Jaa kansio julkisesti ja kopioi linkki. ")
                    ], -1)),
                    _createElementVNode("button", {
                      onClick: enableGalleryEdit,
                      class: "edit-button"
                    }, " Aseta galleria-linkki ")
                  ])
                ]))
        ])
      ]),
      (assignmentImages.value.length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
            _createElementVNode("div", _hoisted_22, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(assignmentImages.value, (imageUrl, index) => {
                return (_openBlock(), _createElementBlock("img", {
                  key: index,
                  src: imageUrl,
                  alt: "Tehtävän kuva",
                  class: "assignment-image",
                  onClick: ($event: any) => (openAssignmentImage(imageUrl))
                }, null, 8, _hoisted_23))
              }), 128))
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_24, [
      _createElementVNode("h2", null, " Palautus: " + _toDisplayString(__props.assignmentData.userName || "Tuntematon opiskelija"), 1),
      (!__props.isGalleryOpen && allImagesCount.value > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(allImages.value, (image) => {
              return (_openBlock(), _createElementBlock("img", {
                key: image.url,
                src: image.url,
                alt: "Kuva",
                class: "default-image",
                onClick: ($event: any) => (openImage(image))
              }, null, 8, _hoisted_26))
            }), 128))
          ]))
        : (__props.isGalleryOpen && allImagesCount.value > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(allImages.value, (image) => {
                return (_openBlock(), _createElementBlock("img", {
                  key: image.url,
                  src: image.url,
                  alt: "Kuva",
                  class: "thumbnail-image",
                  onClick: closeGallery
                }, null, 8, _hoisted_28))
              }), 128))
            ]))
          : (_openBlock(), _createElementBlock("p", _hoisted_29, "Ei kuvia."))
    ]),
    (!!__props.assignmentData.answers?.[0]?.description)
      ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
          _createElementVNode("p", null, _toDisplayString(__props.assignmentData.answers?.[0]?.description || "Ei saatetekstiä."), 1)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_31, [
      _createElementVNode("h2", _hoisted_32, [
        _cache[11] || (_cache[11] = _createTextVNode(" Opettajan huomiot ", -1)),
        (__props.isGradingStarted)
          ? (_openBlock(), _createElementBlock("span", _hoisted_33, "🔒"))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", {
        class: _normalizeClass(["textarea-container", { frozen: __props.isGradingStarted }])
      }, [
        _withDirectives(_createElementVNode("textarea", {
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((teacherNotesText).value = $event)),
          readonly: __props.isGradingStarted,
          placeholder: " - Ensimmäinen havainto\n - Toinen tärkeä asia\n - Kolmas huomio"
        }, null, 8, _hoisted_34), [
          [_vModelText, teacherNotesText.value]
        ])
      ], 2),
      (!__props.isGradingStarted)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            class: _normalizeClass(["start-grading-button", { disabled: !canStartGrading.value }]),
            disabled: !canStartGrading.value,
            onClick: startGrading
          }, " Aloita arviointi ", 10, _hoisted_35))
        : (_openBlock(), _createElementBlock("button", {
            key: 1,
            class: "reset-grading-button",
            onClick: resetGrading
          }, " Aloita arviointi alusta "))
    ])
  ]))
}
}

})